import type { TFunction } from "i18next";
import type { Notification } from "@/domain";

interface NotificationGroup {
  key: "today" | "yesterday" | "this_week" | "earlier";
  label: string;
  notifications: Notification[];
}

/**
 * Groups notifications by time periods: Today, Yesterday, This Week, Earlier
 */
export function groupNotificationsByTime(
  notifications: Notification[],
  t: TFunction,
): NotificationGroup[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekStart = new Date(today);
  weekStart.setDate(weekStart.getDate() - 7);

  const groups: Record<NotificationGroup["key"], Notification[]> = {
    today: [],
    yesterday: [],
    this_week: [],
    earlier: [],
  };

  for (const notification of notifications) {
    const createdAt = new Date(notification.created_at);
    const notificationDate = new Date(
      createdAt.getFullYear(),
      createdAt.getMonth(),
      createdAt.getDate(),
    );

    if (notificationDate.getTime() === today.getTime()) {
      groups.today.push(notification);
    } else if (notificationDate.getTime() === yesterday.getTime()) {
      groups.yesterday.push(notification);
    } else if (notificationDate >= weekStart) {
      groups.this_week.push(notification);
    } else {
      groups.earlier.push(notification);
    }
  }

  // Build result array only for groups with notifications
  const result: NotificationGroup[] = [];

  if (groups.today.length > 0) {
    result.push({
      key: "today",
      label: t("notifications.today"),
      notifications: groups.today,
    });
  }

  if (groups.yesterday.length > 0) {
    result.push({
      key: "yesterday",
      label: t("notifications.yesterday"),
      notifications: groups.yesterday,
    });
  }

  if (groups.this_week.length > 0) {
    result.push({
      key: "this_week",
      label: t("notifications.this_week"),
      notifications: groups.this_week,
    });
  }

  if (groups.earlier.length > 0) {
    result.push({
      key: "earlier",
      label: t("notifications.earlier"),
      notifications: groups.earlier,
    });
  }

  return result;
}

/**
 * Returns date filter bounds for common time ranges
 */
export function getDateFilterBounds(range: "this_week" | "this_month" | "all"): {
  after?: string;
  before?: string;
} {
  if (range === "all") {
    return {};
  }

  const now = new Date();
  const start = new Date(now);

  if (range === "this_week") {
    start.setDate(start.getDate() - 7);
  } else if (range === "this_month") {
    start.setMonth(start.getMonth() - 1);
  }

  return {
    after: start.toISOString(),
    before: undefined,
  };
}
